import { useUIStore, type MailView } from '@/stores/uiStore';
import { useAccountStore } from '@/stores/accountStore';
import { useFolderStore } from '@/stores/folderStore';
import { useEmailStore } from '@/stores/emailStore';
import { useSenderGroupingStore } from '@/stores/senderGroupingStore';
import { useBriefingStore } from '@/stores/briefingStore';
import { safePushState } from '@/lib/iosPwaNavigation';
import { buildMailboxHashPath, MAIL_VIEW_PATHS } from '@/lib/urlRouting';

function currentMailboxPath(): string {
  const accountId = useAccountStore.getState().selectedAccountId;
  const folderId = accountId ? useFolderStore.getState().selectedFolderId() : null;
  return buildMailboxHashPath(accountId, folderId ?? null, null, null);
}

/**
 * Switch to a mail sub-view (tab). Pushes the view's hash route so Back
 * returns to the previous view, and clears reader/drill-down state that
 * belongs to the outgoing view. The mailbox tab restores the current
 * account/folder URL rather than a bare '#/'.
 */
export function navigateToMailView(view: MailView): void {
  const ui = useUIStore.getState();
  if (ui.activeWorkspace === 'mail' && ui.mailView === view) {
    return;
  }

  // A sender group is mailbox-tab state; leaving the tab exits it (skip the
  // reload — the mailbox list refreshes on return through its own effects).
  if (view !== 'mailbox' && useSenderGroupingStore.getState().activeSenderGroup) {
    void useSenderGroupingStore.getState().exitSenderGroup({ skipReload: true });
  }
  // Briefing drill-down scope only means something on the briefing tab.
  if (view !== 'briefing' && useBriefingStore.getState().scope) {
    useBriefingStore.getState().clearScope();
  }
  // Close an open reader so returning to a list view lands on the list.
  useEmailStore.getState().clearSelection();

  const url = view === 'mailbox' ? currentMailboxPath() : MAIL_VIEW_PATHS[view];
  safePushState(url);
  ui.setActiveWorkspace('mail');
  ui.setMailView(view);
}
